import React, { useState, useEffect, useRef, useCallback, FC, CSSProperties } from 'react'
import { unstable_batchedUpdates } from 'react-dom'
import axios from 'axios'
import ReactMarkdown from 'react-markdown'
import { LoadingDots } from '../LoadingDots/LoadingDots'
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal'
import {
  AISparklesIcon,
  ChatbotSparklesIcon,
  NewChatIcon,
  NoChatIcon,
  ChatHistoryIcon,
  CollapseIcon,
  DeleteBinIcon,
} from '../Icons/Icons'
import styles from './Chatbot.module.css'
import { assistantNameFor, useDemoOptions } from './DemoOptionsPanel' /* DEMO ONLY: the assistant's name */

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date | string
  isNew?: boolean
}

interface ChatbotProps {
  teamID: string
  customerID: string
  briefType?: string
  style?: CSSProperties
  className?: string
  webSocketImpl?: typeof WebSocket
  hideViewModeOptions?: boolean
  onCollapse?: () => void
  onExpand?: () => void
}

interface APISession {
  sessionId: string
  updatedAt: string
  messages: Message[]
}

interface MarkdownRendererProps {
  text: string
}

const MarkdownRenderer: FC<MarkdownRendererProps> = ({ text }) => {
  if (!text) return null
  return (
    <div className={styles.markdownBody}>
      <ReactMarkdown>{text}</ReactMarkdown>
    </div>
  )
}

export const generateThreadId = (): string => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return `chat-${window.crypto.randomUUID()}`
  }
  return `chat-${Math.random()
    .toString(36)
    .substring(2, 11)}`
}

const CONNECTION_LOST_MESSAGE = "Sorry, I couldn't reach the assistant just now. Please try again."

const getWelcomeMessage = (briefType: string) =>
  briefType === 'fleet-makeup'
    ? 'I have the background on this customer’s fleet makeup, devices, and print queues. Try a starter prompt...'
    : 'Explore this brief deeper, request help with preparing emails, do general brainstorming, and more.'

const CHAT_PLACEHOLDERS: Record<string, string> = {
  'fleet-makeup': 'Ask about this fleet makeup...',
  security: 'Ask about this Security brief...',
}

// Starters lead with engaging the customer (positioning, outreach, meeting prep) rather than querying the brief:
// the brief is the context, the relationship is the goal.
const getStarterPrompts = (briefType: string) =>
  briefType === 'fleet-makeup'
    ? [
        'How do I make the case for a fleet refresh?',
        'Draft an email for me to send to the customer',
        'Suggest talking points for my next meeting',
      ]
    : [
        'How should I position this from their point of view?',
        'Draft an email for me to send to the customer',
        'Suggest talking points for my next meeting',
      ]

export const Chatbot: FC<ChatbotProps> = ({
  teamID,
  customerID,
  briefType = 'health-check',
  style,
  className,
  webSocketImpl = typeof WebSocket !== 'undefined' ? WebSocket : (undefined as any),
  hideViewModeOptions = false,
  onCollapse,
  onExpand,
}) => {
  const { options: demoOptions } = useDemoOptions() /* DEMO ONLY */
  const assistantName = assistantNameFor(demoOptions.assistantName).label
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: getWelcomeMessage(briefType),
      timestamp: new Date(),
    },
  ])
  const [inputText, setInputText] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  // once a reply has been awaited, the border settles back to thin rather than replaying the page-load spin
  const [hasThought, setHasThought] = useState(false)
  useEffect(() => {
    if (isTyping) setHasThought(true)
  }, [isTyping])
  const [threadId, setThreadId] = useState(() => generateThreadId())
  const [showHistory, setShowHistory] = useState(false)
  const [sessions, setSessions] = useState<APISession[]>([])
  const [isLoadingSessions, setIsLoadingSessions] = useState(false)
  const [sessionIdToDelete, setSessionIdToDelete] = useState<string | null>(null)

  // More menu & View mode state
  const [showMoreMenu, setShowMoreMenu] = useState(false)
  const [viewMode, setViewMode] = useState<'sidebar' | 'compact'>('sidebar')

  useEffect(() => {
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 50)

    return () => clearTimeout(timer)
  }, [viewMode])

  // Mobile & Compact expansion state
  const [isExpanded, setIsExpanded] = useState(false)

  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const moreMenuRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const isFirstRenderRef = useRef(true)
  const socketRef = useRef<WebSocket | null>(null)
  const hasInitializedRef = useRef(false)
  const isMountedRef = useRef(true)
  const scopeKey = JSON.stringify([teamID, customerID, briefType])
  const currentScopeKeyRef = useRef(scopeKey)
  currentScopeKeyRef.current = scopeKey
  const previousScopeKeyRef = useRef(scopeKey)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Close More Menu popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setShowMoreMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Auto-reset expanded state if resized above 1400px while in sidebar mode
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1400 && viewMode === 'sidebar') {
        setIsExpanded(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [viewMode])

  // Auto-grow textarea up to 8 lines
  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return

    textarea.style.height = 'auto'

    const computedStyle = window.getComputedStyle(textarea)
    const lineHeight = parseFloat(computedStyle.lineHeight) || 20
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0

    const maxHeight = lineHeight * 8 + paddingTop + paddingBottom

    if (textarea.scrollHeight > maxHeight) {
      textarea.style.height = `${maxHeight}px`
      textarea.style.overflowY = 'auto'
    } else {
      textarea.style.height = `${textarea.scrollHeight}px`
      textarea.style.overflowY = 'hidden'
    }
  }, [inputText])

  useEffect(() => {
    if (previousScopeKeyRef.current === scopeKey) return
    previousScopeKeyRef.current = scopeKey
    socketRef.current?.close()
    socketRef.current = null
    activeBotMsgIdRef.current = null
    activeBotTextRef.current = ''
    pendingSendRef.current = null
    setMessages([
      {
        id: 'welcome',
        role: 'assistant',
        content: getWelcomeMessage(briefType),
        timestamp: new Date(),
      },
    ])
    setInputText('')
    setIsTyping(false)
    setThreadId(generateThreadId())
    setShowHistory(false)
    setSessions([])
    setIsLoadingSessions(false)
    setSessionIdToDelete(null)
  }, [scopeKey, briefType])

  const fetchSessions = useCallback(
    async (signal?: AbortSignal) => {
      const requestScopeKey = scopeKey
      try {
        setIsLoadingSessions(true)
        const response = await axios.get<APISession[]>(
          `/api/teams/${teamID}/customers/${customerID}/briefs/${briefType}/chatbot/sessions`,
          { signal }
        )
        const sorted = (response?.data || []).sort((a, b) => {
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        })
        if (isMountedRef.current && currentScopeKeyRef.current === requestScopeKey) {
          setSessions(sorted)

          if (!hasInitializedRef.current) {
            hasInitializedRef.current = true
          }
        }
      } catch (err) {
        if (axios.isCancel(err)) {
          return
        }
        if (isMountedRef.current && currentScopeKeyRef.current === requestScopeKey) {
          console.error('Failed to fetch chatbot sessions:', err)
        }
      } finally {
        if (isMountedRef.current && currentScopeKeyRef.current === requestScopeKey) {
          setIsLoadingSessions(false)
        }
      }
    },
    [teamID, customerID, briefType, scopeKey]
  )

  useEffect(() => {
    const controller = new AbortController()
    fetchSessions(controller.signal)
    return () => {
      controller.abort()
    }
  }, [fetchSessions])

  const activeBotMsgIdRef = useRef<string | null>(null)
  const activeBotTextRef = useRef<string>('')
  // a message waiting for a fresh connection to open before it can be sent
  const pendingSendRef = useRef<string | null>(null)

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight
    }
  }

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false
      return
    }
    scrollToBottom()
  }, [messages, isTyping])

  // A reply that can no longer arrive (the connection failed or dropped while it was awaited)
  // is reported in the thread rather than leaving the user's message unanswered.
  const failPendingReply = useCallback(() => {
    pendingSendRef.current = null
    if (!activeBotMsgIdRef.current) return
    const failedMsgId = activeBotMsgIdRef.current
    activeBotMsgIdRef.current = null
    activeBotTextRef.current = ''
    setMessages(prev => [
      ...prev,
      { id: failedMsgId, role: 'assistant', content: CONNECTION_LOST_MESSAGE, timestamp: new Date(), isNew: true },
    ])
    setIsTyping(false)
  }, [])

  const connect = useCallback((): WebSocket | null => {
    if (!webSocketImpl) return null
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsUrl = `${wsProtocol}//${window.location.host}/api/teams/${teamID}/customers/${customerID}/briefs/${briefType}/chatbot`
    const socket = new webSocketImpl(wsUrl)
    socketRef.current = socket
    const isCurrent = () =>
      isMountedRef.current && socketRef.current === socket && currentScopeKeyRef.current === scopeKey

    socket.onopen = () => {
      if (!isCurrent()) return
      console.log('chatbot: WebSocket connection successfully opened')
      if (pendingSendRef.current) {
        socket.send(pendingSendRef.current)
        pendingSendRef.current = null
      }
    }

    socket.onmessage = (event: any) => {
      if (!isCurrent()) return
      try {
        const data = JSON.parse(event.data)
        if (data.text) {
          activeBotTextRef.current += data.text
        }
        if (!data.partial) {
          const finalMsgId = activeBotMsgIdRef.current || `bot-${Date.now()}`
          const completeText = activeBotTextRef.current

          setMessages(prev => [
            ...prev,
            {
              id: finalMsgId,
              role: 'assistant',
              content: completeText,
              timestamp: new Date(),
              isNew: true,
            },
          ])

          setIsTyping(false)
          activeBotMsgIdRef.current = null
          fetchSessions()
        }
      } catch (err) {
        console.error('Failed to parse socket message:', err)
      }
    }

    socket.onerror = (err: any) => {
      if (!isCurrent()) return
      console.error('WebSocket error:', err)
      failPendingReply()
    }

    socket.onclose = () => {
      if (!isCurrent()) return
      console.log('chatbot: WebSocket connection closed')
      failPendingReply()
    }

    return socket
  }, [teamID, customerID, briefType, scopeKey, fetchSessions, webSocketImpl, failPendingReply])

  useEffect(() => {
    const socket = connect()
    return () => {
      // the current socket may be a replacement opened after this one dropped
      for (const s of [socket, socketRef.current === socket ? null : socketRef.current]) {
        if (!s) continue
        s.onopen = null
        s.onmessage = null
        s.onerror = null
        s.onclose = null
        s.close()
      }
      socketRef.current = null
    }
  }, [connect])

  const handleSend = async (textToSend: string) => {
    if (isTyping) return

    if (onExpand) {
      onExpand()
    }

    const trimmed = textToSend.trim()

    // Respond with default prompt if input is empty
    if (!trimmed) {
      setMessages(prev => [
        ...prev,
        {
          id: `bot-${Date.now()}`,
          role: 'assistant',
          content: 'How can I help you?',
          timestamp: new Date(),
          isNew: true,
        },
      ])
      return
    }

    const userMsgId = `user-${Date.now()}`
    const botMsgId = `bot-${Date.now()}`

    activeBotMsgIdRef.current = botMsgId
    activeBotTextRef.current = ''

    setMessages(prev => [
      ...prev,
      {
        id: userMsgId,
        role: 'user',
        content: trimmed,
        timestamp: new Date(),
      },
    ])
    setInputText('')
    setIsTyping(true)

    const payload = JSON.stringify({ text: trimmed, sessionId: threadId })
    const socket = socketRef.current
    if (socket && webSocketImpl && socket.readyState === webSocketImpl.OPEN) {
      socket.send(payload)
      return
    }
    // The connection dropped (backend restart, idle timeout, sleep): send once a fresh one opens.
    pendingSendRef.current = payload
    if (socket && webSocketImpl && socket.readyState === webSocketImpl.CONNECTING) return
    console.warn('chatbot: WebSocket not open, reconnecting. State:', socket?.readyState)
    if (!connect()) failPendingReply()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend(inputText)
    }
  }

  const handleLoadChat = (sess: APISession) => {
    setThreadId(sess.sessionId)
    if (sess.messages && sess.messages.length > 0) {
      setMessages(sess.messages)
    } else {
      setMessages([
        {
          id: 'welcome',
          role: 'assistant',
          content: getWelcomeMessage(briefType),
          timestamp: new Date(),
        },
      ])
    }
    setShowHistory(false)
  }

  const handleNewChat = () => {
    const newId = generateThreadId()
    setThreadId(newId)
    setMessages([
      {
        id: 'welcome',
        role: 'assistant',
        content: getWelcomeMessage(briefType),
        timestamp: new Date(),
      },
    ])
    setShowHistory(false)
  }

  const handleDeleteChat = async (id: string) => {
    const requestScopeKey = scopeKey
    try {
      await axios.delete(`/api/teams/${teamID}/customers/${customerID}/briefs/${briefType}/chatbot/sessions/${id}`)
      if (currentScopeKeyRef.current !== requestScopeKey) return
      await fetchSessions()
      if (isMountedRef.current && currentScopeKeyRef.current === requestScopeKey && id === threadId) {
        handleNewChat()
      }
    } catch (err) {
      if (isMountedRef.current) {
        console.error('Failed to delete session:', err)
      }
    }
  }

  // Sidebar <-> compact: morph Mission Control between the panel and the bubble with the View
  // Transitions API (styles in Chatbot.module.css and BriefShell.module.css). Browsers without
  // it, and anyone who prefers reduced motion, get the instant switch.
  const switchViewMode = (next: 'sidebar' | 'compact') => {
    // outside a React event handler, React 16 commits a batch synchronously, so the DOM is in its
    // new state by the time the transition takes its "after" snapshot
    const apply = () => {
      unstable_batchedUpdates(() => {
        setViewMode(next)
        setIsExpanded(false)
        setShowMoreMenu(false)
      })
    }
    const doc = document as Document & {
      startViewTransition?: (update: () => void) => { finished: Promise<void> }
    }
    const container = containerRef.current
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (next === viewMode || !doc.startViewTransition || !container || reduceMotion) {
      apply()
      return
    }
    const root = document.documentElement
    root.dataset.mcMorph = next === 'compact' ? 'to-compact' : 'to-sidebar'
    // named only for the transition, so another Mission Control on the page can't clash with it
    container.style.setProperty('view-transition-name', 'mission-control')
    const cleanUp = () => {
      delete root.dataset.mcMorph
      container.style.removeProperty('view-transition-name')
    }
    doc.startViewTransition(apply).finished.then(cleanUp, cleanUp)
  }

  const suggestionChips = getStarterPrompts(briefType)

  // Suggestion chips stay visible until a user message is sent
  const hasUserMessages = messages.some(msg => msg.role === 'user')

  return (
    <div
      ref={containerRef}
      className={`${styles.chatbotContainer}${
        isTyping ? ` ${styles.thinking}` : hasThought ? ` ${styles.settled}` : ''
      }${isExpanded ? ' isExpanded' : ''}${viewMode === 'compact' ? ` ${styles.compactView}` : ''}${
        className ? ` ${className}` : ''
      }`}
      onClick={() => {
        if (!isExpanded && (viewMode === 'compact' || (typeof window !== 'undefined' && window.innerWidth <= 1400))) {
          setIsExpanded(true)
        }
      }}
      /* eslint-disable-next-line react/forbid-dom-props */
      style={style}
    >
      {/* Header */}
      <div className={styles.chatbotHeader}>
        <div className={styles.chatbotTitleArea}>
          <span className={styles.sparkle}>
            <i className='icon twentyfour flex-noshrink'>
              <ChatbotSparklesIcon />
            </i>
          </span>
          <div>
            <h3 className={styles.chatbotTitle}>{assistantName}</h3>
          </div>
        </div>

        <div className={styles.headerActions}>
          {/* Popover Menu Trigger */}
          <div className={styles.moreMenuWrapper} ref={moreMenuRef}>
            <button
              onClick={e => {
                e.stopPropagation()
                setShowMoreMenu(prev => !prev)
              }}
              className={`${styles.actionButton}${showMoreMenu ? ` ${styles.actionButtonActive}` : ''}`}
              title='More options'
              aria-label='More options'
              aria-expanded={showMoreMenu}
            >
              <svg viewBox='0 0 16 16' height='16' width='16'>
                <path
                  fill='currentColor'
                  fillRule='evenodd'
                  d='M10 2a2 2 0 1 1 -4 0 2 2 0 0 1 4 0ZM8 10a2 2 0 1 0 0 -4 2 2 0 0 0 0 4ZM8 16a2 2 0 1 0 0 -4A2 2 0 0 0 8 16Z'
                  clipRule='evenodd'
                  strokeWidth='1.6'
                ></path>
              </svg>
            </button>

            {/* More Menu Popover */}
            {showMoreMenu && (
              <div className={styles.moreMenuPopover}>
                <button
                  onClick={e => {
                    e.stopPropagation()
                    handleNewChat()
                    setShowMoreMenu(false)
                  }}
                  className={styles.popoverItem}
                >
                  <i className='icon sixteen flex-noshrink'>
                    <NewChatIcon />
                  </i>
                  <span>New chat</span>
                </button>

                <button
                  onClick={e => {
                    e.stopPropagation()
                    if (onExpand) {
                      onExpand()
                    }
                    setShowHistory(prev => !prev)
                    setShowMoreMenu(false)
                  }}
                  className={styles.popoverItem}
                >
                  <i className='icon sixteen flex-noshrink'>
                    <ChatHistoryIcon />
                  </i>
                  <span>{showHistory ? 'Back to chat' : 'Chat history'}</span>
                </button>

                {/* View options visible only when not explicitly hidden */}
                {!hideViewModeOptions && (
                  <>
                    <div className={`${styles.popoverDivider} ${styles.viewModeOption}`} />

                    <button
                      onClick={e => {
                        e.stopPropagation()
                        switchViewMode('sidebar')
                      }}
                      className={`${styles.popoverItem} ${styles.viewModeOption}${
                        viewMode === 'sidebar' ? ` ${styles.popoverItemActive}` : ''
                      }`}
                    >
                      <svg
                        width='16'
                        height='16'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2.5'
                      >
                        <rect x='3' y='3' width='18' height='18' rx='2' />
                        <line x1='9' y1='3' x2='9' y2='21' />
                      </svg>
                      <span>Sidebar view</span>
                    </button>

                    <button
                      onClick={e => {
                        e.stopPropagation()
                        switchViewMode('compact')
                      }}
                      className={`${styles.popoverItem} ${styles.viewModeOption}${
                        viewMode === 'compact' ? ` ${styles.popoverItemActive}` : ''
                      }`}
                    >
                      <svg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <path
                          d='M14.251 2.99799L14.251 9.74799L21.001 9.74799'
                          stroke='currentColor'
                          strokeWidth='2.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                        <path
                          d='M14.251 9.74799L22.5 1.5'
                          stroke='currentColor'
                          strokeWidth='2.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                        <path
                          d='M2 12H12V22H2V12Z'
                          stroke='currentColor'
                          strokeWidth='2.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                      <span>Compact view</span>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Collapse button when expanded or when onCollapse callback is provided */}
          {(isExpanded || onCollapse) && (
            <button
              onClick={e => {
                e.stopPropagation()
                setIsExpanded(false)
                handleNewChat()
                if (onCollapse) {
                  onCollapse()
                }
              }}
              className={`${styles.actionButton} collapseButton`}
              title='Collapse chat'
              aria-label='Collapse chat'
            >
              <i className='icon twelve flex-noshrink'>
                <CollapseIcon />
              </i>
            </button>
          )}
        </div>
      </div>

      {showHistory ? (
        /* Chat History Panel */
        <div className={styles.historyPanel}>
          <h5>Previous chats</h5>

          {isLoadingSessions ? (
            <div className={styles.loadingSessionsWrapper}>
              <div className={styles.loadingSessionsSpinner}>
                <LoadingDots />
              </div>
            </div>
          ) : sessions.length === 0 ? (
            <div className={styles.noSessionsWrapper}>
              <i className='icon twentyfour dip'>
                <NoChatIcon />
              </i>
              <h5 className='no-margin-bottom'>No previous chats found</h5>
              <p>Any messages you send will appear here.</p>
            </div>
          ) : (
            <div className={styles.sessionsList}>
              {sessions.map(sess => {
                const firstUserMsg = sess.messages?.find(m => m.role === 'user')?.content || 'Empty Chat'
                const formattedDate = new Date(sess.updatedAt).toLocaleString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
                const isActive = sess.sessionId === threadId

                return (
                  <div
                    key={sess.sessionId}
                    className={`${styles.sessionItem}${isActive ? ` ${styles.sessionItemActive}` : ''}`}
                    onClick={() => handleLoadChat(sess)}
                  >
                    <div className={styles.sessionItemDetails}>
                      <h5 className={styles.sessionItemText}>{firstUserMsg}</h5>
                      <small className={styles.sessionItemDate}>{formattedDate}</small>
                    </div>

                    <button
                      onClick={e => {
                        e.stopPropagation()
                        setSessionIdToDelete(sess.sessionId)
                      }}
                      className={styles.deleteSessionBtn}
                      title='Delete chat'
                    >
                      <i className='icon sixteen flex-noshrink'>
                        <DeleteBinIcon />
                      </i>
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      ) : (
        /* Main Chat View */
        <>
          <div ref={messagesContainerRef} className={styles.messagesContainer}>
            {messages.map((msg, index) => {
              const isUser = msg.role === 'user'
              const isNewAssistant = !isUser && msg.isNew

              return (
                <React.Fragment key={msg.id}>
                  <div
                    className={`${styles.messageWrapper} ${
                      isUser ? styles.messageWrapperUser : styles.messageWrapperAssistant
                    }${isNewAssistant ? ` ${styles.messageWrapperNew}` : ''}`}
                  >
                    <div
                      className={`${styles.messageBubble} ${
                        isUser ? styles.messageBubbleUser : styles.messageBubbleAssistant
                      }`}
                    >
                      {isUser ? msg.content : <MarkdownRenderer text={msg.content} />}
                    </div>
                  </div>

                  {/* Render suggestion chips directly after the welcome message */}
                  {index === 0 && !hasUserMessages && (
                    <div className={styles.suggestionsWrapper}>
                      {suggestionChips.map(chip => (
                        <button
                          key={chip}
                          onClick={() => {
                            if (onExpand) {
                              onExpand()
                            }
                            handleSend(chip)
                          }}
                          className={styles.suggestionChip}
                        >
                          {chip}
                        </button>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              )
            })}

            {isTyping && (
              <div className={styles.typingIndicatorWrapper}>
                <div className={styles.typingIndicator}>
                  <span className={styles.typingDot}></span>
                  <span className={styles.typingDot}></span>
                  <span className={styles.typingDot}></span>
                </div>
              </div>
            )}
          </div>

          <form
            onSubmit={e => {
              e.preventDefault()
              handleSend(inputText)
            }}
            className={styles.inputForm}
          >
            <textarea
              ref={textareaRef}
              rows={3}
              value={inputText}
              onChange={e => {
                setInputText(e.target.value)
                if (e.target.value && onExpand) {
                  onExpand()
                }
              }}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                if (onExpand) {
                  onExpand()
                }
              }}
              placeholder={CHAT_PLACEHOLDERS[briefType] ?? 'Ask anything about this Health check...'}
              className={styles.inputField}
            />
            <button type='submit' className={`btn ${styles.sendBtn}`}>
              <i className='icon twelve flex-noshrink'>
                <AISparklesIcon />
              </i>
              <span>Ask</span>
            </button>
          </form>
        </>
      )}

      {sessionIdToDelete !== null && (
        <ConfirmationModal
          title='Delete chat thread'
          message='Are you sure you want to delete this chat thread?'
          onClose={() => setSessionIdToDelete(null)}
          onYes={() => {
            handleDeleteChat(sessionIdToDelete)
          }}
        />
      )}
    </div>
  )
}
